import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PosmDeliverySchedule } from '../models/posm-execution.models';
import { PosmService } from '../posm.service';
import { PosmScheduleCompletionReportComponent } from './posm-schedule-completion-report.component';
import { FieldResourcesPanelComponent } from '../../field-work/field-resources-panel.component';

@Component({
    selector: 'app-posm-delivery-schedule-detail',
    standalone: true,
    imports: [
        CommonModule,
        DatePipe,
        ButtonModule,
        DynamicDialogModule,
        ProgressBarModule,
        TagModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent,
        PosmScheduleCompletionReportComponent,
        FieldResourcesPanelComponent
    ],
    providers: [],
    template: `
        <app-page-shell>
            <app-page-detail-hero
                icon="pi pi-calendar"
                eyebrow="POSM Delivery Schedule"
                [title]="schedule()?.scheduleNumber || ('Schedule #' + scheduleId())"
                [subtitle]="heroSubtitle()">
                <div tags>
                    <p-tag [value]="schedule()?.readinessStatus || 'PENDING'" [severity]="readinessSeverity(schedule()?.readinessStatus)"></p-tag>
                    <p-tag [value]="coverageModeLabel(schedule())" [severity]="coverageModeSeverity(schedule())"></p-tag>
                    <p-tag [value]="schedule()?.progressStage || schedule()?.status || 'PENDING'" [severity]="statusSeverity(schedule()?.progressStage || schedule()?.status)"></p-tag>
                    <p-tag [value]="schedule()?.dueStatus || 'UNSCHEDULED'" [severity]="dueSeverity(schedule()?.dueStatus)"></p-tag>
                </div>
                <div actions>
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="back()"></p-button>
                    <p-button label="Project" icon="pi pi-briefcase" severity="secondary" [outlined]="true" [disabled]="!schedule()?.projectId" (click)="openProject()"></p-button>
                    @if (canEdit()) {
                        <p-button label="Edit Schedule" icon="pi pi-pencil" [disabled]="!schedule()?.projectId" (click)="editSchedule()"></p-button>
                    }
                    <p-button label="Refresh" icon="pi pi-refresh" (click)="loadSchedule()"></p-button>
                </div>
            </app-page-detail-hero>

            @if (loading()) {
                <app-page-data-panel title="Loading" subtitle="Loading delivery schedule details."></app-page-data-panel>
            } @else if (!schedule()) {
                <app-page-data-panel title="Not Found" subtitle="Delivery schedule details could not be loaded."></app-page-data-panel>
            } @else {
                <section class="detail-grid">
                    <app-page-data-panel title="Schedule Progress" subtitle="Current execution maturity and next action.">
                        <div class="progress-header">
                            <strong>{{ schedule()?.progressPercent || 0 }}%</strong>
                            <span>{{ schedule()?.statusReason || 'No status reason available.' }}</span>
                        </div>
                        <p-progressBar [value]="schedule()?.progressPercent || 0" [showValue]="false"></p-progressBar>
                        @if (schedule()?.nextAction) {
                            <div class="next-action">{{ schedule()?.nextAction }}</div>
                        }
                        @if (schedule()?.mappingWarnings?.length) {
                            <ul class="warning-list">
                                @for (warning of schedule()?.mappingWarnings; track warning) {
                                    <li>{{ warning }}</li>
                                }
                            </ul>
                        }
                    </app-page-data-panel>

                    <app-page-data-panel title="Project Context" subtitle="Header values are resolved from the POSM project.">
                        <div class="facts">
                            <div><span>Project</span><strong>{{ schedule()?.projectName || '-' }}</strong></div>
                            <div><span>Project Number</span><strong>{{ schedule()?.projectNumber || '-' }}</strong></div>
                            <div><span>Client</span><strong>{{ schedule()?.clientName || '-' }}</strong></div>
                            <div><span>Schedule Number</span><strong>{{ schedule()?.scheduleNumber || '-' }}</strong></div>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Store & Location" subtitle="Target store is linked to LogiLink Location.">
                        <div class="facts">
                            <div><span>{{ schedule()?.location?.retailer?.name ? 'Retailer / Store Chain' : 'Imported Customer Name' }}</span><strong>{{ schedule()?.location?.retailer?.name || schedule()?.customerName || '-' }}</strong></div>
                            <div><span>Store</span><strong>{{ schedule()?.location?.name || schedule()?.storeName || '-' }}</strong></div>
                            <div><span>Store Number</span><strong>{{ schedule()?.storeNumber || schedule()?.location?.externalLocationReference || '-' }}</strong></div>
                            <div><span>City</span><strong>{{ schedule()?.city || '-' }}</strong></div>
                            <div><span>Location ID</span><strong>{{ schedule()?.locationId || '-' }}</strong></div>
                            <div><span>Contact</span><strong>{{ schedule()?.contactNumber || '-' }}</strong></div>
                            <div><span>Contact Override</span><strong>{{ schedule()?.contactNumberOverride || '-' }}</strong></div>
                        </div>
                        <div class="section-actions">
                            <p-button label="Open Location" icon="pi pi-map-marker" severity="secondary" [outlined]="true" [disabled]="!schedule()?.location?.id" (click)="openLocation()"></p-button>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Warehousing Item" subtitle="Stand, coverage mode, and source are resolved from Warehousing and POSM source planning.">
                        <div class="facts">
                            <div><span>Item</span><strong>{{ schedule()?.inventoryItem?.itemName || schedule()?.standName || '-' }}</strong></div>
                            <div><span>Item Number</span><strong>{{ schedule()?.inventoryItem?.itemNumber || '-' }}</strong></div>
                            <div><span>SKU</span><strong>{{ schedule()?.inventoryItem?.skuNumber || '-' }}</strong></div>
                            <div><span>Size</span><strong>{{ schedule()?.standSize || '-' }}</strong></div>
                            <div><span>Quantity</span><strong>{{ schedule()?.standQty || schedule()?.projectItem?.quantity || 0 }}</strong></div>
                            <div><span>Source Warehouse</span><strong>{{ schedule()?.sourceWarehouseName || '-' }}</strong></div>
                            <div><span>Coverage</span><strong>{{ schedule()?.reservationStatus || '-' }} | {{ coverageModeLabel(schedule()) }}</strong></div>
                        </div>
                        <div class="section-actions">
                            <p-button label="Open Item" icon="pi pi-box" severity="secondary" [outlined]="true" [disabled]="!schedule()?.inventoryItem?.id" (click)="openItem()"></p-button>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Supplier & Planning" subtitle="Supplier is a CRM client; date can be overridden at row level.">
                        <div class="facts">
                            <div><span>Supplier</span><strong>{{ schedule()?.supplierClientName || schedule()?.supplier || '-' }}</strong></div>
                            <div><span>Supplier Client ID</span><strong>{{ schedule()?.supplierClientId || '-' }}</strong></div>
                            <div><span>Planned Installation</span><strong>{{ schedule()?.plannedInstallationDate | date:'dd MMM yyyy' }}</strong></div>
                            <div><span>Override Date</span><strong>{{ schedule()?.plannedInstallationDateOverride | date:'dd MMM yyyy' }}</strong></div>
                            <div class="wide"><span>Comment</span><strong>{{ schedule()?.comment || '-' }}</strong></div>
                        </div>
                        <div class="section-actions">
                            <p-button label="Open Supplier" icon="pi pi-building" severity="secondary" [outlined]="true" [disabled]="!schedule()?.supplierClientId" (click)="openSupplier()"></p-button>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Execution & Transport" subtitle="Task, order release, shipment, delivery, truck, and driver links.">
                        <div class="facts">
                            <div><span>Task</span><strong>{{ schedule()?.installationTaskId ? ('Task #' + schedule()?.installationTaskId) : '-' }}</strong></div>
                            <div><span>Task Status</span><strong>{{ schedule()?.taskStatus || '-' }}</strong></div>
                            <div><span>Order Release</span><strong>{{ schedule()?.releaseNumber || (schedule()?.transportReleaseId ? ('Release #' + schedule()?.transportReleaseId) : '-') }}</strong></div>
                            <div><span>Release Status</span><strong>{{ schedule()?.releaseStatus || schedule()?.transportStatus || '-' }}</strong></div>
                            <div><span>Validation</span><strong>{{ schedule()?.validationStatus || '-' }}</strong></div>
                            <div><span>Shipment</span><strong>{{ schedule()?.shipmentStatus || '-' }}</strong></div>
                            <div><span>Delivery</span><strong>{{ schedule()?.deliveryNumber || (schedule()?.deliveryId ? ('Delivery #' + schedule()?.deliveryId) : '-') }}</strong></div>
                            <div><span>Actual Delivery</span><strong>{{ schedule()?.actualDeliveryDate | date:'dd MMM yyyy' }}</strong></div>
                            <div><span>Truck</span><strong>{{ schedule()?.truckName || '-' }}</strong></div>
                            <div><span>Driver</span><strong>{{ schedule()?.driverName || '-' }}</strong></div>
                        </div>
                    </app-page-data-panel>

                    <app-page-data-panel title="Import Audit" subtitle="Legacy workbook metadata is kept for traceability.">
                        <div class="facts">
                            <div><span>Source Sheet</span><strong>{{ schedule()?.sourceSheetName || '-' }}</strong></div>
                            <div><span>Source Row</span><strong>{{ schedule()?.sourceRowNumber || '-' }}</strong></div>
                            <div class="wide"><span>Source Row Hash</span><strong>{{ schedule()?.sourceRowHash || '-' }}</strong></div>
                        </div>
                    </app-page-data-panel>
                </section>

                <app-field-resources-panel
                    targetType="POSM_DELIVERY_SCHEDULE"
                    [targetId]="scheduleId() || undefined"
                    [targetReference]="schedule()?.scheduleNumber"
                    [locationId]="schedule()?.locationId || schedule()?.location?.id"
                    [locationName]="schedule()?.location?.name || schedule()?.storeName">
                </app-field-resources-panel>

                <app-posm-schedule-completion-report
                    [projectId]="schedule()?.projectId"
                    [scheduleId]="scheduleId()"
                    [owningCompanyId]="owningCompanyId()">
                </app-posm-schedule-completion-report>
            }
        </app-page-shell>
    `,
    styles: [`
        .detail-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 1rem;
        }

        .facts {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.75rem;
        }

        .facts > div {
            min-width: 0;
            border: 1px solid var(--surface-border);
            border-radius: 6px;
            padding: 0.75rem;
            background: var(--surface-50);
        }

        .facts .wide {
            grid-column: 1 / -1;
        }

        .facts span,
        .progress-header span {
            display: block;
            color: var(--text-color-secondary);
            font-size: 0.78rem;
        }

        .facts strong {
            display: block;
            margin-top: 0.25rem;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .progress-header {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.75rem;
        }

        .progress-header strong {
            color: var(--primary-color);
            font-size: 2rem;
        }

        .next-action {
            margin-top: 0.75rem;
            color: var(--primary-color);
            font-weight: 700;
        }

        .warning-list {
            margin: 0.75rem 0 0;
            padding-left: 1rem;
            color: #92400e;
        }

        .section-actions {
            display: flex;
            justify-content: flex-end;
            margin-top: 0.75rem;
        }

        app-posm-schedule-completion-report,
        app-field-resources-panel {
            display: block;
            margin-top: 1rem;
        }

        @media (max-width: 960px) {
            .detail-grid,
            .facts {
                grid-template-columns: 1fr;
            }
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmDeliveryScheduleDetailComponent implements OnInit {
    private posmService = inject(PosmService);
    private sessionStorage = inject(SessionStorageService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private permissionService = inject(PermissionService);

    schedule = signal<PosmDeliverySchedule | null>(null);
    loading = signal(false);
    scheduleId = signal<number | null>(null);
    owningCompanyId = signal<number | null>(null);

    ngOnInit(): void {
        this.scheduleId.set(Number(this.route.snapshot.paramMap.get('id')));
        const companyId = this.sessionStorage.getCustomer()?.company?.id || this.sessionStorage.getCompany()?.id;
        this.owningCompanyId.set(companyId || null);
        this.loadSchedule();
    }

    loadSchedule(): void {
        const id = this.scheduleId();
        const companyId = this.owningCompanyId();
        if (!id || !companyId) return;
        this.loading.set(true);
        this.posmService.getDeliveryScheduleByLineId(id, companyId).subscribe({
            next: row => {
                this.schedule.set(this.normalizeDates(row));
                this.loading.set(false);
            },
            error: () => {
                this.schedule.set(null);
                this.loading.set(false);
            }
        });
    }

    heroSubtitle(): string {
        const row = this.schedule();
        return [row?.projectNumber, row?.projectName, row?.location?.name || row?.storeName].filter(Boolean).join(' | ');
    }

    coverageModeLabel(row: PosmDeliverySchedule | null | undefined): string {
        return `${row?.coverageMode || ''}`.toUpperCase() === 'LOGICAL' ? 'Logical Coverage' : 'Physical Reservation';
    }

    coverageModeSeverity(row: PosmDeliverySchedule | null | undefined): string {
        return `${row?.coverageMode || ''}`.toUpperCase() === 'LOGICAL' ? 'info' : 'secondary';
    }

    back(): void {
        this.router.navigate(['/posm/executions/delivery-schedules']);
    }

    openProject(): void {
        const row = this.schedule();
        if (row?.projectId) this.router.navigate(['/posm/executions', row.projectId]);
    }

    canEdit(): boolean {
        return this.permissionService.hasPermission(app_permissions.posm.projects.edit);
    }

    editSchedule(): void {
        const row = this.schedule();
        if (row?.projectId && row.id) {
            this.router.navigate(['/posm/executions', row.projectId, 'delivery-schedule', row.id, 'edit'],
                { queryParams: { returnUrl: this.router.url } });
        }
    }

    openLocation(): void {
        const row = this.schedule();
        if (row?.location?.id) {
            this.router.navigate(['/location/view', row.location.id]);
        }
    }

    openItem(): void {
        const row = this.schedule();
        if (row?.inventoryItem?.id) this.router.navigate(['/warehousing/items/view', row.inventoryItem.id]);
    }

    openSupplier(): void {
        const row = this.schedule();
        if (row?.supplierClientId) this.router.navigate(['/crm/clients', row.supplierClientId]);
    }

    readinessSeverity(value?: string | null): string {
        const normalized = `${value || ''}`.toUpperCase();
        if (normalized === 'READY') return 'success';
        if (normalized.includes('MAPPING')) return 'warning';
        return 'info';
    }

    statusSeverity(status?: string | null): string {
        const value = `${status || ''}`.toUpperCase();
        if (value.includes('DELIVERED') || value.includes('COMPLETE') || value.includes('APPROVED')) return 'success';
        if (value.includes('ERROR') || value.includes('EXCEPTION') || value.includes('REJECT')) return 'danger';
        if (value.includes('PENDING') || value.includes('SCHEDULED')) return 'warning';
        return 'info';
    }

    dueSeverity(status?: string | null): string {
        const value = `${status || ''}`.toUpperCase();
        if (value === 'OVERDUE') return 'danger';
        if (value === 'DUE_TODAY') return 'warning';
        return 'info';
    }

    private normalizeDates(row: PosmDeliverySchedule): PosmDeliverySchedule {
        return {
            ...row,
            plannedInstallationDate: this.toDate(row.plannedInstallationDate),
            plannedInstallationDateOverride: this.toDate(row.plannedInstallationDateOverride),
            actualDeliveryDate: this.toDate(row.actualDeliveryDate),
            releaseGeneratedAt: this.toDate(row.releaseGeneratedAt),
            releasedToTruckPlanAt: this.toDate(row.releasedToTruckPlanAt),
            statusDate: this.toDate(row.statusDate)
        };
    }

    private toDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
}
